/**
 * TypeScript types for the kalmtide/1 preset schema.
 *
 * These mirror schema/kalmtide.schema.json (the machine-checkable source of
 * truth) — if the two ever disagree, the JSON Schema wins and this file is the
 * bug. Every consumer (web dsp, editor, workers) should import these types and
 * obtain values only via validatePreset(), which narrows unknown → Preset.
 */

export type Goal = "sleep" | "meditate" | "focus" | "obe";
export type Curve = "hold" | "linear" | "exp";
export type Waveform = "sine" | "triangle" | "square" | "sawtooth";
export type NoiseColor = "white" | "pink" | "brown";
export type LayerType =
  | "binaural"
  | "monaural"
  | "isochronic"
  | "noise"
  | "ambience"
  | "voice";

/** [start, end] ramp pair over the segment's time span — NOT left/right. */
export type RampPair = [number, number];

export interface Segment {
  t0: number;
  t1: number;
  carrierHz: RampPair;
  beatHz: RampPair;
  curve: Curve;
}

export interface EnvelopePoint {
  t: number;
  gainDb: number;
}

interface LayerBase {
  id: string;
  gainDb: number;
  pan?: number;
}

export interface BinauralLayer extends LayerBase {
  type: "binaural";
  waveform?: Waveform;
  segments: Segment[];
}

export interface MonauralLayer extends LayerBase {
  type: "monaural";
  waveform?: Waveform;
  segments: Segment[];
}

export interface IsochronicLayer extends LayerBase {
  type: "isochronic";
  waveform?: Waveform;
  dutyCycle: number;
  gateShape: "raisedCosine";
  segments: Segment[];
}

export interface NoiseLayer extends LayerBase {
  type: "noise";
  color: NoiseColor;
  envelope?: EnvelopePoint[];
}

export interface AmbienceLayer extends LayerBase {
  type: "ambience";
  asset: string;
  loop?: boolean;
}

export interface VoiceLayer extends LayerBase {
  type: "voice";
  source: string;
  startSec?: number;
  /** Restart the asset every N seconds after startSec, until durationSec. */
  repeatEverySec?: number;
  ducking?: {
    targetLayerTypes: Exclude<LayerType, "voice">[];
    duckDb: number;
  };
}

export type Layer =
  | BinauralLayer
  | MonauralLayer
  | IsochronicLayer
  | NoiseLayer
  | AmbienceLayer
  | VoiceLayer;

/** A layer that carries entrainment segments (binaural / monaural / isochronic). */
export type SegmentedLayer = BinauralLayer | MonauralLayer | IsochronicLayer;

export interface PresetMeta {
  id: string;
  name: string;
  goal: Goal;
  author?: string;
  durationSec: number;
  created?: string;
}

export interface Master {
  gainDb: number;
  fadeInSec: number;
  fadeOutSec: number;
  limiter: boolean;
}

export interface Preset {
  schema: "kalmtide/1";
  meta: PresetMeta;
  allowFallback?: boolean;
  layers: Layer[];
  master: Master;
}

export function isSegmentedLayer(layer: Layer): layer is SegmentedLayer {
  return (
    layer.type === "binaural" ||
    layer.type === "monaural" ||
    layer.type === "isochronic"
  );
}
